<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisWhatsappOperationsAPI from 'dashboard/api/laisWhatsappOperations';
import { input } from '../utils/minhaLaisOptions';

const props = defineProps({
  whatsappConnections: { type: Array, default: () => [] },
  canManage: { type: Boolean, default: false },
  defaultTarget: { type: String, default: '' },
  preferredInboxId: { type: [Number, String], default: null },
});

const emit = defineEmits(['openConnection']);
const router = useRouter();
const account = useAccount();

const phoneField = {
  name: 'number',
  label: 'Número ou conversa',
  helper: 'Use o telefone com DDI, por exemplo 5521988156977.',
  placeholder: '5521988156977',
  required: true,
};

const groupField = {
  name: 'group_jid',
  label: 'Grupo ou comunidade',
  source: 'groups',
  placeholder: '120000000000000000@g.us',
  required: true,
};

const channelField = {
  name: 'newsletter_jid',
  label: 'Canal',
  source: 'newsletters',
  placeholder: 'canal@newsletter',
  required: true,
};

const jidField = {
  name: 'jid',
  label: 'Contato WhatsApp',
  helper: 'Use o telefone com DDI ou o JID completo.',
  placeholder: '5521988156977',
  required: true,
};

const fileField = ({ accept, kind, required = true, label = 'Arquivo' }) => ({
  name: 'base64',
  type: 'file',
  label,
  helper: 'O arquivo é enviado diretamente pela sessão conectada.',
  accept,
  fileName: 'file_name',
  mimetype: 'mimetype',
  kind,
  required,
});

const messageContextFields = [
  {
    name: 'forwarded',
    label: 'Rótulo de encaminhada',
    type: 'select',
    default: 'unchanged',
    context: true,
    helper:
      'Adicione o rótulo, remova-o de um payload protobuf ou não envie essa alteração.',
    options: [
      { value: 'unchanged', label: 'Não alterar' },
      { value: 'true', label: 'Adicionar rótulo Encaminhada' },
      { value: 'false', label: 'Remover rótulo Encaminhada' },
    ],
  },
  {
    name: 'forwarding_score',
    label: 'Nível de encaminhamento',
    type: 'number',
    min: 1,
    max: 5,
    integer: true,
    context: true,
    helper: 'Use um valor de 1 a 5 somente ao adicionar o rótulo.',
  },
  {
    name: 'mentioned_jids',
    label: 'Menções',
    type: 'list',
    context: true,
    helper: 'Um JID de usuário por linha. Máximo de 100 menções.',
    placeholder: '5521988156978@s.whatsapp.net',
  },
  {
    name: 'reply_message_id',
    label: 'ID da mensagem respondida',
    context: true,
    helper: 'Preencha junto com o JID do remetente e o conteúdo citado.',
  },
  {
    name: 'reply_sender_jid',
    label: 'Remetente da mensagem respondida',
    context: true,
    placeholder: '5521988156977@s.whatsapp.net',
  },
  {
    name: 'reply_text',
    label: 'Texto da mensagem respondida',
    type: 'textarea',
    context: true,
    helper: 'Use para responder citando uma mensagem de texto.',
  },
  {
    name: 'reply_message',
    label: 'Mensagem respondida em protobuf (JSON)',
    type: 'textarea',
    context: true,
    helper: 'Use no lugar do texto para citar mídia ou outro formato protobuf.',
    placeholder: '{"imageMessage":{"caption":"Foto original"}}',
  },
];

const messageDeliveryFields = [
  {
    name: 'delay',
    label: 'Atraso antes do envio (ms)',
    type: 'number',
    min: 0,
    max: 60000,
    integer: true,
    helper: 'Opcional. O gateway aceita até 60.000 ms.',
  },
  {
    name: 'client_message_id',
    label: 'ID da mensagem no cliente',
    maxLength: 128,
    helper:
      'Opcional. Use um ID estável para correlacionar ou deduplicar o envio.',
  },
];

const clientMessageIdField = {
  name: 'client_message_id',
  label: 'ID da mensagem no cliente',
  maxLength: 128,
  helper: 'Opcional. Use um ID estável para correlacionar a reação.',
};

const withMessageContext = fields => [
  ...fields,
  ...messageContextFields,
  ...messageDeliveryFields,
];
const withMessageDelivery = fields => [...fields, ...messageDeliveryFields];

const operationAreas = [
  {
    id: 'messages',
    label: 'Mensagens',
    icon: 'i-ph-chat-circle-text-bold',
    actions: [
      {
        id: 'send_text',
        label: 'Mensagem de texto',
        description: 'Envie uma mensagem direta para qualquer número ou grupo.',
        fields: withMessageContext([
          phoneField,
          { name: 'text', label: 'Mensagem', type: 'textarea', required: true },
          {
            name: 'link_preview',
            label: 'Solicitar prévia de link',
            type: 'checkbox',
            default: false,
            helper: 'Use quando o texto contiver uma URL http ou https.',
          },
        ]),
      },
      {
        id: 'send_media',
        label: 'Foto, vídeo ou figurinha',
        description: 'Envie uma imagem, vídeo ou figurinha com legenda.',
        fields: withMessageContext([
          phoneField,
          fileField({ accept: 'image/*,video/*,.webp', kind: 'kind' }),
          {
            name: 'kind',
            label: 'Tipo de mídia',
            type: 'select',
            default: 'image',
            options: [
              { value: 'image', label: 'Foto' },
              { value: 'video', label: 'Vídeo' },
              { value: 'sticker', label: 'Figurinha' },
            ],
          },
          { name: 'caption', label: 'Legenda', type: 'textarea' },
          {
            name: 'view_once',
            label: 'Visualização única',
            type: 'checkbox',
            default: false,
            helper: 'Disponível para fotos e vídeos.',
          },
          {
            name: 'gif_playback',
            label: 'Reproduzir como GIF',
            type: 'checkbox',
            default: false,
            helper: 'Disponível para vídeos.',
          },
          {
            name: 'ptv',
            label: 'Vídeo redondo (PTV)',
            type: 'checkbox',
            default: false,
            helper:
              'Disponível para vídeos; não combine com GIF ou visualização única.',
          },
        ]),
      },
      {
        id: 'send_document',
        label: 'Documento',
        description: 'Envie PDF, planilha, apresentação ou outro arquivo.',
        fields: withMessageContext([
          phoneField,
          fileField({ accept: '*/*' }),
          {
            name: 'caption',
            label: 'Mensagem de acompanhamento',
            type: 'textarea',
          },
        ]),
      },
      {
        id: 'send_audio',
        label: 'Áudio',
        description: 'Envie áudio comum ou uma mensagem de voz.',
        fields: withMessageContext([
          phoneField,
          {
            name: 'audio',
            type: 'file',
            label: 'Arquivo de áudio',
            accept: 'audio/*',
            fileName: null,
            mimetype: 'mimetype',
            required: true,
          },
          {
            name: 'ptt',
            label: 'Enviar como mensagem de voz',
            type: 'checkbox',
            default: true,
          },
        ]),
      },
      {
        id: 'send_location',
        label: 'Localização',
        description:
          'Compartilhe uma localização com coordenadas, nome e endereço.',
        fields: withMessageContext([
          phoneField,
          {
            name: 'latitude',
            label: 'Latitude',
            type: 'number',
            step: 'any',
            required: true,
          },
          {
            name: 'longitude',
            label: 'Longitude',
            type: 'number',
            step: 'any',
            required: true,
          },
          { name: 'name', label: 'Nome do local' },
          { name: 'address', label: 'Endereço' },
          { name: 'url', label: 'Link do mapa', type: 'url' },
        ]),
      },
      {
        id: 'send_contact',
        label: 'Contato',
        description: 'Envie um contato que pode ser salvo pelo destinatário.',
        fields: withMessageContext([
          phoneField,
          { name: 'display_name', label: 'Nome do contato', required: true },
          {
            name: 'phone',
            label: 'Telefone do contato',
            placeholder: '5521988156977',
            required: true,
            requiredWhen: form => !form.vcard.trim(),
          },
          {
            name: 'vcard',
            label: 'vCard do contato',
            type: 'textarea',
            helper:
              'Opcional. Use no lugar do telefone para enviar campos completos do contato.',
            placeholder:
              'BEGIN:VCARD\nVERSION:3.0\nFN:Ana Silva\nTEL;TYPE=CELL:+5521988156977\nEND:VCARD',
          },
        ]),
      },
      {
        id: 'send_contacts',
        label: 'Vários contatos',
        description:
          'Envie vários contatos que podem ser salvos pelo destinatário.',
        fields: withMessageContext([
          phoneField,
          { name: 'display_name', label: 'Título da lista' },
          {
            name: 'contacts',
            label: 'Contatos',
            type: 'list',
            helper:
              'Use esta lista ou o JSON avançado abaixo: Nome | telefone com DDI.',
            placeholder: 'Ana Silva | 5521988156977',
            required: true,
            requiredWhen: form => !form.contacts_json.trim(),
            inputOnly: true,
          },
          {
            name: 'contacts_json',
            label: 'Contatos avançados (JSON)',
            type: 'textarea',
            inputOnly: true,
            helper:
              'Opcional. Use para enviar vCards completos em vários contatos.',
            placeholder:
              '[{"display_name":"Ana Silva","vcard":"BEGIN:VCARD\\nVERSION:3.0\\nFN:Ana Silva\\nEND:VCARD"}]',
          },
        ]),
      },
      {
        id: 'send_buttons',
        label: 'Botões',
        description: 'Envie até três opções de resposta rápida.',
        fields: withMessageContext([
          phoneField,
          { name: 'header', label: 'Cabeçalho' },
          { name: 'text', label: 'Mensagem', type: 'textarea', required: true },
          { name: 'footer', label: 'Rodapé' },
          {
            name: 'buttons',
            label: 'Botões',
            type: 'list',
            helper:
              'Uma linha por botão: identificador | título. Máximo de três.',
            placeholder: 'morning | Manhã',
            required: true,
          },
        ]),
      },
      {
        id: 'send_list',
        label: 'Lista de opções',
        description: 'Envie uma lista organizada em seções selecionáveis.',
        fields: withMessageContext([
          phoneField,
          { name: 'title', label: 'Título' },
          { name: 'text', label: 'Mensagem', type: 'textarea', required: true },
          { name: 'button_text', label: 'Texto do botão', required: true },
          { name: 'footer', label: 'Rodapé' },
          {
            name: 'sections',
            label: 'Opções da lista',
            type: 'list',
            helper:
              'Uma linha por opção: Seção | identificador | título | descrição opcional.',
            placeholder: 'Horários | morning | Manhã | 09:00 às 12:00',
            required: true,
          },
        ]),
      },
      {
        id: 'send_live_location',
        label: 'Localização em tempo real',
        description:
          'Compartilhe uma posição atualizável com precisão e velocidade opcionais.',
        fields: withMessageContext([
          phoneField,
          {
            name: 'latitude',
            label: 'Latitude',
            type: 'number',
            step: 'any',
            required: true,
          },
          {
            name: 'longitude',
            label: 'Longitude',
            type: 'number',
            step: 'any',
            required: true,
          },
          {
            name: 'accuracy_in_meters',
            label: 'Precisão em metros',
            type: 'number',
            integer: true,
          },
          {
            name: 'speed_mps',
            label: 'Velocidade (m/s)',
            type: 'number',
            step: 'any',
          },
          {
            name: 'heading',
            label: 'Direção (0 a 360)',
            type: 'number',
            integer: true,
          },
          {
            name: 'sequence_number',
            label: 'Número da atualização',
            type: 'number',
            integer: true,
            helper:
              'Use ao publicar atualizações sucessivas da mesma localização ao vivo.',
          },
          {
            name: 'time_offset',
            label: 'Deslocamento de tempo (segundos)',
            type: 'number',
            integer: true,
            helper:
              'Opcional. Informe o tempo relativo da atualização de localização.',
          },
          { name: 'caption', label: 'Legenda', type: 'textarea' },
        ]),
      },
      {
        id: 'send_poll',
        label: 'Enquete',
        description: 'Crie uma enquete com até doze opções.',
        fields: withMessageContext([
          phoneField,
          { name: 'name', label: 'Pergunta', required: true },
          {
            name: 'options',
            label: 'Opções',
            type: 'list',
            helper: 'Digite uma opção por linha.',
            required: true,
          },
          {
            name: 'selectable_options_count',
            label: 'Respostas permitidas por pessoa',
            type: 'number',
            default: 1,
            min: 1,
            integer: true,
          },
        ]),
      },
      {
        id: 'react_message',
        label: 'Reagir a mensagem',
        description: 'Adicione ou remova uma reação em uma mensagem existente.',
        fields: withMessageDelivery([
          phoneField,
          {
            name: 'message_id',
            label: 'Identificador da mensagem',
            required: true,
          },
          {
            name: 'reaction',
            label: 'Emoji da reação',
            placeholder: '👍',
            required: true,
          },
          {
            name: 'sender_jid',
            label: 'JID do remetente',
            helper: 'Obrigatório para mensagens recebidas.',
          },
        ]),
      },
      {
        id: 'edit_message',
        label: 'Editar mensagem enviada',
        description: 'Atualize o texto de uma mensagem própria já enviada.',
        fields: withMessageDelivery([
          phoneField,
          {
            name: 'message_id',
            label: 'Identificador da mensagem',
            required: true,
          },
          {
            name: 'text',
            label: 'Novo texto',
            type: 'textarea',
            required: true,
          },
        ]),
      },
      {
        id: 'revoke_message',
        label: 'Apagar mensagem enviada',
        description: 'Revogue uma mensagem para todos os participantes.',
        danger: true,
        fields: withMessageDelivery([
          phoneField,
          {
            name: 'message_id',
            label: 'Identificador da mensagem',
            required: true,
          },
          {
            name: 'sender_jid',
            label: 'JID do remetente',
            helper: 'Use para mensagens recebidas.',
          },
        ]),
      },
      {
        id: 'send_poll_vote',
        label: 'Votar em enquete',
        description:
          'Registre o voto da conta conectada em uma enquete existente.',
        fields: withMessageDelivery([
          phoneField,
          {
            name: 'message_id',
            label: 'Identificador da enquete',
            required: true,
          },
          {
            name: 'sender_jid',
            label: 'JID de quem criou a enquete',
            required: true,
          },
          {
            name: 'options',
            label: 'Opções escolhidas',
            type: 'list',
            required: true,
          },
          {
            name: 'is_from_me',
            label: 'A enquete foi enviada por esta conta',
            type: 'checkbox',
            default: false,
          },
        ]),
      },
      {
        id: 'send_raw_message',
        label: 'Mensagem protobuf avançada',
        description:
          'Envie qualquer WAWebProtobufsE2E.Message em JSON protobuf.',
        fields: withMessageContext([
          phoneField,
          {
            name: 'message',
            label: 'Mensagem protobuf (JSON)',
            type: 'textarea',
            placeholder:
              '{"templateMessage":{"hydratedTemplate":{"hydratedContentText":"Olá"}}}',
            required: true,
          },
        ]),
      },
      {
        id: 'get_sticker_pack',
        label: 'Consultar pacote de figurinhas',
        description:
          'Veja metadados e figurinhas de um pacote sem expor chaves de mídia.',
        fields: [
          {
            name: 'pack_id',
            label: 'ID do pacote',
            helper: 'Use o identificador do pacote de figurinhas do WhatsApp.',
            required: true,
          },
        ],
      },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    icon: 'i-ph-circle-notch-bold',
    actions: [
      {
        id: 'post_status_text',
        label: 'Publicar status de texto',
        description: 'Publique um novo texto no Status do WhatsApp.',
        fields: withMessageDelivery([
          {
            name: 'text',
            label: 'Texto do status',
            type: 'textarea',
            required: true,
          },
        ]),
      },
      {
        id: 'post_status_media',
        label: 'Publicar foto, vídeo ou figurinha',
        description:
          'Publique uma foto, vídeo ou figurinha no Status do WhatsApp.',
        fields: withMessageDelivery([
          fileField({ accept: 'image/*,video/*,.webp', kind: 'kind' }),
          {
            name: 'kind',
            label: 'Tipo de mídia',
            type: 'select',
            default: 'image',
            options: [
              { value: 'image', label: 'Foto' },
              { value: 'video', label: 'Vídeo' },
              { value: 'sticker', label: 'Figurinha' },
            ],
          },
          { name: 'caption', label: 'Legenda', type: 'textarea' },
        ]),
      },
      {
        id: 'get_status_privacy',
        label: 'Ver privacidade do Status',
        description: 'Atualize os dados de quem pode ver seus status.',
        fields: [],
      },
    ],
  },
  {
    id: 'account',
    label: 'Perfil e privacidade',
    icon: 'i-ph-user-gear-bold',
    actions: [
      {
        id: 'get_profile',
        label: 'Consultar perfil',
        description: 'Veja seu perfil ou a prévia de outro contato.',
        fields: [
          {
            name: 'jid',
            label: 'Contato',
            helper: 'Deixe em branco para consultar seu próprio perfil.',
          },
          {
            name: 'preview',
            label: 'Consultar prévia do contato',
            type: 'checkbox',
            default: false,
          },
        ],
      },
      {
        id: 'set_profile_status',
        label: 'Atualizar recado',
        description: 'Altere o texto exibido no perfil da conta conectada.',
        fields: [
          {
            name: 'status_message',
            label: 'Novo recado',
            type: 'textarea',
            required: true,
          },
        ],
      },
      {
        id: 'set_presence',
        label: 'Ficar online ou indisponível',
        description: 'Atualize a presença da conta conectada.',
        fields: [
          {
            name: 'state',
            label: 'Presença',
            type: 'select',
            default: 'available',
            options: [
              { value: 'available', label: 'Online' },
              { value: 'unavailable', label: 'Indisponível' },
            ],
            required: true,
          },
        ],
      },
      {
        id: 'set_privacy',
        label: 'Alterar privacidade',
        description:
          'Configure última vez, online, foto, status, recibos e outros controles.',
        fields: [
          {
            name: 'setting',
            label: 'O que deseja controlar',
            type: 'select',
            default: 'last_seen',
            options: [
              { value: 'last_seen', label: 'Visto por último' },
              { value: 'online', label: 'Online' },
              { value: 'profile', label: 'Foto do perfil' },
              { value: 'status', label: 'Status' },
              { value: 'read_receipts', label: 'Confirmações de leitura' },
              { value: 'group_add', label: 'Quem pode adicionar a grupos' },
              { value: 'call_add', label: 'Quem pode adicionar a chamadas' },
              { value: 'messages', label: 'Mensagens de desconhecidos' },
              { value: 'defense', label: 'Proteção da conta' },
              { value: 'stickers', label: 'Figurinhas de desconhecidos' },
            ],
            required: true,
          },
          {
            name: 'value',
            label: 'Quem pode ver ou usar',
            type: 'select',
            default: 'contacts',
            options: [
              { value: 'all', label: 'Todos' },
              { value: 'contacts', label: 'Meus contatos' },
              { value: 'contact_blacklist', label: 'Contatos, exceto...' },
              {
                value: 'contact_allowlist',
                label: 'Apenas contatos selecionados',
              },
              { value: 'none', label: 'Ninguém' },
              { value: 'match_last_seen', label: 'Igual ao visto por último' },
              { value: 'known', label: 'Pessoas conhecidas' },
              { value: 'on', label: 'Ativar' },
              { value: 'off', label: 'Desativar' },
            ],
            required: true,
          },
        ],
      },
      {
        id: 'get_privacy',
        label: 'Ver privacidade atual',
        description: 'Atualize o resumo de privacidade desta conta.',
        fields: [],
      },
      {
        id: 'get_blocklist',
        label: 'Ver contatos bloqueados',
        description: 'Atualize a lista de bloqueios.',
        fields: [],
      },
      {
        id: 'update_blocklist',
        label: 'Bloquear ou desbloquear',
        description: 'Altere o bloqueio de um contato.',
        fields: [
          jidField,
          {
            name: 'action',
            label: 'Ação',
            type: 'select',
            default: 'block',
            options: [
              { value: 'block', label: 'Bloquear' },
              { value: 'unblock', label: 'Desbloquear' },
            ],
            required: true,
          },
        ],
      },
      {
        id: 'set_disappearing',
        label: 'Mensagens temporárias',
        description: 'Defina o prazo padrão ou de uma conversa específica.',
        fields: [
          {
            name: 'chat_jid',
            label: 'Conversa',
            helper: 'Deixe em branco para definir o padrão da conta.',
          },
          {
            name: 'duration_seconds',
            label: 'Duração',
            type: 'select',
            default: 604800,
            options: [
              { value: 0, label: 'Desativadas' },
              { value: 86400, label: '24 horas' },
              { value: 604800, label: '7 dias' },
              { value: 7776000, label: '90 dias' },
            ],
            required: true,
            integer: true,
          },
        ],
      },
      {
        id: 'get_contact_qr',
        label: 'Meu QR de contato',
        description:
          'Recupere o link de QR que permite iniciar conversa com esta conta.',
        fields: [
          {
            name: 'revoke',
            label: 'Revogar o link atual e gerar outro',
            type: 'checkbox',
            default: false,
          },
        ],
      },
      {
        id: 'resolve_contact_qr',
        label: 'Consultar QR de contato',
        description:
          'Identifique a conta por trás de um link ou QR de contato.',
        fields: [
          { name: 'link', label: 'Link do QR', type: 'url', required: true },
        ],
      },
    ],
  },
  {
    id: 'contacts',
    label: 'Contatos e conversa',
    icon: 'i-ph-users-three-bold',
    actions: [
      {
        id: 'set_typing',
        label: 'Indicador de digitação',
        description: 'Mostre que a conta está digitando ou gravando áudio.',
        fields: [
          { name: 'chat_jid', label: 'Conversa', required: true },
          {
            name: 'state',
            label: 'Estado',
            type: 'select',
            default: 'composing',
            options: [
              { value: 'composing', label: 'Digitando' },
              { value: 'paused', label: 'Parar indicador' },
            ],
            required: true,
          },
          {
            name: 'media',
            label: 'Tipo',
            type: 'select',
            default: 'text',
            options: [
              { value: 'text', label: 'Texto' },
              { value: 'audio', label: 'Áudio' },
            ],
          },
        ],
      },
      {
        id: 'mark_read',
        label: 'Marcar mensagens como lidas',
        description: 'Envie recibo de leitura ou de áudio reproduzido.',
        fields: [
          { name: 'chat_jid', label: 'Conversa', required: true },
          {
            name: 'message_ids',
            label: 'Identificadores das mensagens',
            type: 'list',
            required: true,
          },
          { name: 'sender_jid', label: 'JID do remetente' },
          {
            name: 'receipt_type',
            label: 'Confirmação',
            type: 'select',
            default: 'read',
            options: [
              { value: 'read', label: 'Lida' },
              { value: 'played', label: 'Áudio reproduzido' },
            ],
          },
        ],
      },
      {
        id: 'lookup_users',
        label: 'Verificar números',
        description:
          'Confira se números ou JIDs estão disponíveis no WhatsApp.',
        fields: [
          {
            name: 'phones',
            label: 'Telefones',
            type: 'list',
            helper: 'Um número com DDI por linha.',
          },
          {
            name: 'jids',
            label: 'JIDs',
            type: 'list',
            helper: 'Um JID por linha.',
          },
        ],
      },
      {
        id: 'get_business_profile',
        label: 'Perfil comercial',
        description: 'Consulte endereço, horário e categorias de uma empresa.',
        fields: [jidField],
      },
      {
        id: 'resolve_business_message_link',
        label: 'Consultar link comercial',
        description:
          'Resolva um link wa.me/message e obtenha o contato e a mensagem sugerida.',
        fields: [
          {
            name: 'link',
            label: 'Link comercial',
            type: 'url',
            required: true,
          },
        ],
      },
      {
        id: 'list_bots',
        label: 'Listar bots disponíveis',
        description:
          'Consulte bots, perfis, comandos e sugestões da conta conectada.',
        fields: [],
      },
      {
        id: 'get_user_devices',
        label: 'Dispositivos de um contato',
        description: 'Liste os dispositivos vinculados a uma conta.',
        fields: [
          { name: 'jids', label: 'Contatos', type: 'list', required: true },
        ],
      },
      {
        id: 'subscribe_presence',
        label: 'Acompanhar presença',
        description:
          'Atualize online e visto por último deste contato na tela de atendimento.',
        fields: [jidField],
      },
    ],
  },
  {
    id: 'groups',
    label: 'Grupos e comunidades',
    icon: 'i-ph-users-four-bold',
    actions: [
      {
        id: 'list_groups',
        label: 'Atualizar grupos',
        description: 'Busque os grupos e comunidades desta conta.',
        fields: [],
      },
      {
        id: 'create_group',
        label: 'Criar grupo ou comunidade',
        description:
          'Crie um grupo comum ou uma comunidade com participantes iniciais.',
        fields: [
          {
            name: 'name',
            label: 'Nome',
            helper: 'Até 25 caracteres.',
            maxLength: 25,
            required: true,
          },
          {
            name: 'participants',
            label: 'Participantes',
            type: 'list',
            helper:
              'Um telefone com DDI por linha. Comunidades podem começar sem participantes.',
            required: true,
            requiredWhen: form => !form.community,
          },
          { name: 'description', label: 'Descrição', type: 'textarea' },
          {
            name: 'community',
            label: 'Criar como comunidade',
            type: 'checkbox',
            default: false,
          },
          {
            name: 'parent_jid',
            label: 'Comunidade principal',
            source: 'communities',
            helper: 'Use apenas ao criar um grupo vinculado.',
          },
        ],
      },
      {
        id: 'get_group',
        label: 'Ver detalhes',
        description: 'Atualize o perfil e os participantes de um grupo.',
        fields: [groupField],
      },
      {
        id: 'leave_group',
        label: 'Sair do grupo',
        description: 'Remova a conta conectada de um grupo.',
        danger: true,
        fields: [groupField],
      },
      {
        id: 'update_group_participants',
        label: 'Gerenciar participantes',
        description: 'Adicione, remova ou altere administradores.',
        fields: [
          groupField,
          {
            name: 'action',
            label: 'Ação',
            type: 'select',
            default: 'add',
            options: [
              { value: 'add', label: 'Adicionar' },
              { value: 'remove', label: 'Remover' },
              { value: 'promote', label: 'Tornar administrador' },
              { value: 'demote', label: 'Remover administração' },
            ],
            required: true,
          },
          {
            name: 'participants',
            label: 'Participantes',
            type: 'list',
            required: true,
          },
        ],
      },
      {
        id: 'update_group_settings',
        label: 'Configurar grupo',
        description:
          'Atualize nome, descrição, anúncios, permissões e aprovação de entrada.',
        fields: [
          groupField,
          { name: 'name', label: 'Novo nome' },
          { name: 'description', label: 'Nova descrição', type: 'textarea' },
          {
            name: 'announce',
            label: 'Somente administradores enviam mensagens',
            type: 'checkbox',
            optional: true,
          },
          {
            name: 'locked',
            label: 'Somente administradores alteram dados do grupo',
            type: 'checkbox',
            optional: true,
          },
          {
            name: 'join_approval',
            label: 'Exigir aprovação para entrada',
            type: 'checkbox',
            optional: true,
          },
          {
            name: 'member_add_mode',
            label: 'Quem pode adicionar participantes',
            helper: 'Deixe em branco para manter a configuração atual.',
            type: 'select',
            options: [
              { value: 'admin_add', label: 'Somente administradores' },
              { value: 'all_member_add', label: 'Todos os participantes' },
            ],
          },
        ],
      },
      {
        id: 'update_group_photo',
        label: 'Alterar foto do grupo',
        description: 'Envie uma nova imagem para o grupo ou comunidade.',
        fields: [
          groupField,
          fileField({ accept: 'image/*', label: 'Nova foto' }),
        ],
      },
      {
        id: 'get_group_invite_info',
        label: 'Consultar convite',
        description: 'Veja detalhes de um convite antes de entrar no grupo.',
        fields: [
          {
            name: 'link',
            label: 'Link do convite',
            type: 'url',
            required: true,
          },
        ],
      },
      {
        id: 'join_group_with_link',
        label: 'Entrar por convite',
        description: 'Entre em um grupo usando o link de convite.',
        fields: [
          {
            name: 'link',
            label: 'Link do convite',
            type: 'url',
            required: true,
          },
        ],
      },
      {
        id: 'get_group_invite_link',
        label: 'Link do convite',
        description: 'Consulte ou renove o link de convite de um grupo.',
        fields: [
          groupField,
          {
            name: 'reset',
            label: 'Invalidar link antigo e gerar outro',
            type: 'checkbox',
            default: false,
          },
        ],
      },
      {
        id: 'get_group_join_requests',
        label: 'Pedidos para entrar',
        description: 'Veja quem está aguardando aprovação.',
        fields: [groupField],
      },
      {
        id: 'update_group_join_requests',
        label: 'Decidir pedidos de entrada',
        description: 'Aprove ou recuse participantes pendentes.',
        fields: [
          groupField,
          {
            name: 'action',
            label: 'Decisão',
            type: 'select',
            default: 'approve',
            options: [
              { value: 'approve', label: 'Aprovar' },
              { value: 'reject', label: 'Recusar' },
            ],
            required: true,
          },
          {
            name: 'participants',
            label: 'Participantes',
            type: 'list',
            required: true,
          },
        ],
      },
      {
        id: 'get_community_participants',
        label: 'Participantes vinculados',
        description: 'Liste participantes da comunidade e de seus grupos.',
        fields: [
          {
            ...groupField,
            name: 'community_jid',
            label: 'Comunidade',
            source: 'communities',
          },
        ],
      },
      {
        id: 'list_subgroups',
        label: 'Grupos da comunidade',
        description: 'Liste os grupos vinculados a uma comunidade.',
        fields: [
          {
            ...groupField,
            name: 'community_jid',
            label: 'Comunidade',
            source: 'communities',
          },
        ],
      },
      {
        id: 'link_subgroup',
        label: 'Vincular grupo à comunidade',
        description: 'Adicione um grupo existente a uma comunidade.',
        fields: [
          {
            ...groupField,
            name: 'community_jid',
            label: 'Comunidade',
            source: 'communities',
          },
          { ...groupField, name: 'group_jid', label: 'Grupo a vincular' },
        ],
      },
      {
        id: 'unlink_subgroup',
        label: 'Desvincular grupo',
        description: 'Remova um grupo de uma comunidade sem apagá-lo.',
        danger: true,
        fields: [
          {
            ...groupField,
            name: 'community_jid',
            label: 'Comunidade',
            source: 'communities',
          },
          { ...groupField, name: 'group_jid', label: 'Grupo a desvincular' },
        ],
      },
    ],
  },
  {
    id: 'channels',
    label: 'Canais',
    icon: 'i-ph-broadcast-bold',
    actions: [
      {
        id: 'list_newsletters',
        label: 'Atualizar canais',
        description: 'Busque os canais acompanhados por esta conta.',
        fields: [],
      },
      {
        id: 'create_newsletter',
        label: 'Criar canal',
        description: 'Crie um novo canal no WhatsApp.',
        fields: [
          {
            name: 'name',
            label: 'Nome do canal',
            helper: 'Até 100 caracteres.',
            maxLength: 100,
            required: true,
          },
          { name: 'description', label: 'Descrição', type: 'textarea' },
          {
            name: 'picture',
            type: 'file',
            label: 'Foto do canal',
            accept: 'image/*',
          },
        ],
      },
      {
        id: 'accept_newsletter_tos',
        label: 'Aceitar termos para canais',
        description:
          'Confirme expressamente os termos informados pelo WhatsApp antes de criar canais.',
        danger: true,
        fields: [
          {
            name: 'notice_id',
            label: 'ID do aviso',
            maxLength: 128,
            required: true,
          },
          {
            name: 'stage',
            label: 'Etapa do aviso',
            maxLength: 128,
            required: true,
          },
          {
            name: 'confirmed',
            label: 'Li e aceito estes termos em nome da conta',
            type: 'checkbox',
            default: false,
          },
        ],
      },
      {
        id: 'post_newsletter_text',
        label: 'Publicar texto',
        description:
          'Publique uma atualização de texto para os seguidores do canal.',
        fields: withMessageDelivery([
          channelField,
          {
            name: 'text',
            label: 'Publicação',
            type: 'textarea',
            required: true,
          },
        ]),
      },
      {
        id: 'post_newsletter_media',
        label: 'Publicar foto ou vídeo',
        description: 'Publique uma foto ou vídeo com legenda no canal.',
        fields: withMessageDelivery([
          channelField,
          fileField({ accept: 'image/*,video/*', kind: 'kind' }),
          {
            name: 'kind',
            label: 'Tipo de publicação',
            type: 'select',
            default: 'image',
            options: [
              { value: 'image', label: 'Foto' },
              { value: 'video', label: 'Vídeo' },
            ],
          },
          { name: 'caption', label: 'Legenda', type: 'textarea' },
        ]),
      },
      {
        id: 'get_newsletter',
        label: 'Ver detalhes do canal',
        description: 'Atualize dados públicos de um canal.',
        fields: [channelField],
      },
      {
        id: 'get_newsletter_by_invite',
        label: 'Consultar canal por convite',
        description:
          'Veja os dados públicos de um canal por seu link whatsapp.com/channel.',
        fields: [
          {
            name: 'link',
            label: 'Link do canal',
            type: 'url',
            required: true,
          },
        ],
      },
      {
        id: 'follow_newsletter',
        label: 'Seguir ou deixar de seguir',
        description: 'Altere o acompanhamento de um canal.',
        fields: [
          channelField,
          {
            name: 'followed',
            label: 'Seguir este canal',
            type: 'checkbox',
            default: true,
          },
        ],
      },
      {
        id: 'mute_newsletter',
        label: 'Silenciar canal',
        description: 'Ative ou desative as notificações de um canal.',
        fields: [
          channelField,
          {
            name: 'mute',
            label: 'Silenciar notificações',
            type: 'checkbox',
            default: true,
          },
        ],
      },
      {
        id: 'subscribe_newsletter_live_updates',
        label: 'Atualizações ao vivo',
        description: 'Assine atualizações em tempo real de um canal.',
        fields: [channelField],
      },
      {
        id: 'get_newsletter_messages',
        label: 'Ver mensagens do canal',
        description: 'Carregue as mensagens mais recentes de um canal.',
        fields: [
          channelField,
          {
            name: 'count',
            label: 'Quantidade',
            type: 'number',
            default: 20,
            min: 1,
            max: 100,
            integer: true,
          },
          {
            name: 'before',
            label: 'Antes do ID de servidor',
            type: 'number',
            integer: true,
          },
        ],
      },
      {
        id: 'get_newsletter_updates',
        label: 'Atualizações das mensagens',
        description:
          'Consulte contagens recentes de reações e visualizações publicadas no canal.',
        fields: [
          channelField,
          {
            name: 'count',
            label: 'Quantidade',
            type: 'number',
            default: 20,
            min: 1,
            max: 100,
            integer: true,
          },
          {
            name: 'since',
            label: 'Atualizações desde',
            type: 'datetime-local',
          },
          {
            name: 'after',
            label: 'Após o ID de servidor',
            type: 'number',
            integer: true,
          },
        ],
      },
      {
        id: 'mark_newsletter_viewed',
        label: 'Marcar como vistas',
        description: 'Confirme a visualização de mensagens de um canal.',
        fields: [
          channelField,
          {
            name: 'server_ids',
            label: 'IDs das mensagens',
            type: 'list',
            required: true,
            integer: true,
          },
        ],
      },
      {
        id: 'react_newsletter',
        label: 'Reagir a mensagem do canal',
        description: 'Envie uma reação em uma mensagem de canal.',
        fields: [
          channelField,
          {
            name: 'server_id',
            label: 'ID da mensagem',
            type: 'number',
            required: true,
            integer: true,
          },
          {
            name: 'reaction',
            label: 'Emoji',
            placeholder: '❤️',
            required: true,
          },
          clientMessageIdField,
        ],
      },
    ],
  },
];

const selectedConnectionId = ref(null);
const snapshot = ref(null);
const isLoading = ref(false);
const isExecuting = ref(false);
const selectedAreaId = ref('messages');
const selectedOperationId = ref('send_text');
const form = reactive({});
const selectedFiles = reactive({});
const result = ref(null);

const openCatalogManager = () => {
  router.push(
    account.accountScopedRoute(
      'minha_lais_whatsapp_catalog_index',
      {},
      selectedConnectionId.value
        ? { connection_id: selectedConnectionId.value }
        : {}
    )
  );
};

const privacyValues = {
  group_add: ['all', 'contacts', 'contact_blacklist', 'none'],
  last_seen: ['all', 'contacts', 'contact_blacklist', 'none'],
  status: ['all', 'contacts', 'contact_blacklist', 'none'],
  profile: ['all', 'contacts', 'contact_blacklist', 'none'],
  read_receipts: ['all', 'none'],
  online: ['all', 'match_last_seen'],
  call_add: ['all', 'known'],
  messages: ['all', 'contacts'],
  defense: ['on', 'off'],
  stickers: ['contacts', 'contact_allowlist', 'none'],
};

const privacyValueLabels = {
  all: 'Todos',
  contacts: 'Meus contatos',
  contact_blacklist: 'Contatos, exceto...',
  contact_allowlist: 'Apenas contatos selecionados',
  none: 'Ninguém',
  match_last_seen: 'Igual ao visto por último',
  known: 'Pessoas conhecidas',
  on: 'Ativar',
  off: 'Desativar',
};

const connectionOptions = computed(() =>
  props.whatsappConnections.map(connection => ({
    id: connection.id,
    inboxId: connection.inbox_id,
    label:
      connection.profile_name ||
      connection.phone_number ||
      connection.instance_name,
  }))
);

const supportedOperations = computed(() =>
  Object.values(snapshot.value?.capabilities || {}).flat()
);

const selectedArea = computed(
  () =>
    operationAreas.find(area => area.id === selectedAreaId.value) ||
    operationAreas[0]
);

const visibleActions = computed(() =>
  selectedArea.value.actions.filter(
    action => !snapshot.value || supportedOperations.value.includes(action.id)
  )
);

const selectedOperation = computed(
  () =>
    operationAreas
      .flatMap(area => area.actions)
      .find(action => action.id === selectedOperationId.value) ||
    visibleActions.value[0]
);

const groupEntries = computed(() => snapshot.value?.groups?.data?.groups || []);
const communityEntries = computed(() =>
  groupEntries.value.filter(group => group.is_community)
);
const newsletterEntries = computed(
  () => snapshot.value?.newsletters?.data?.newsletters || []
);
const groupCount = computed(() => groupEntries.value.length);
const communityCount = computed(() => communityEntries.value.length);
const newsletterCount = computed(() => newsletterEntries.value.length);
const catalogProductCount = computed(
  () => snapshot.value?.catalog?.products?.length || 0
);
const isConnected = computed(
  () => snapshot.value?.connection?.connected === true
);
const canExecute = computed(
  () =>
    props.canManage &&
    Boolean(selectedConnectionId.value) &&
    isConnected.value &&
    !isExecuting.value
);
const resultSummary = computed(() => {
  if (!result.value) return '';

  const data = result.value;
  const identifier =
    data.group_jid ||
    data.newsletter_jid ||
    data.jid ||
    data.message_id ||
    data.id ||
    data.link;

  return identifier
    ? `Identificador: ${identifier}`
    : 'A ação foi concluída e os dados foram atualizados.';
});

const fieldOptions = field => {
  if (field.name === 'value' && selectedOperation.value?.id === 'set_privacy') {
    return (privacyValues[form.setting] || []).map(value => ({
      value,
      label: privacyValueLabels[value],
    }));
  }

  if (field.source === 'groups') {
    return groupEntries.value.map(group => ({
      value: group.jid || group.group_jid,
      label: group.name || group.subject || group.jid || group.group_jid,
    }));
  }

  if (field.source === 'communities') {
    return communityEntries.value.map(group => ({
      value: group.jid || group.group_jid,
      label: group.name || group.subject || group.jid || group.group_jid,
    }));
  }

  if (field.source === 'newsletters') {
    return newsletterEntries.value.map(newsletter => ({
      value: newsletter.jid || newsletter.newsletter_jid,
      label:
        newsletter.name ||
        newsletter.title ||
        newsletter.jid ||
        newsletter.newsletter_jid,
    }));
  }

  return field.options || [];
};

const resetForm = () => {
  Object.keys(form).forEach(key => delete form[key]);
  Object.keys(selectedFiles).forEach(key => delete selectedFiles[key]);
  result.value = null;

  selectedOperation.value?.fields.forEach(field => {
    if (field.default !== undefined) {
      form[field.name] = field.default;
    } else if (
      props.defaultTarget &&
      ['number', 'chat_jid', 'jid'].includes(field.name)
    ) {
      form[field.name] = props.defaultTarget;
    } else if (props.defaultTarget && field.name === 'phones') {
      form[field.name] = props.defaultTarget;
    } else if (field.type === 'checkbox') {
      form[field.name] = field.optional ? undefined : false;
    } else {
      form[field.name] = '';
    }
  });
};

const selectArea = area => {
  selectedAreaId.value = area.id;
  selectedOperationId.value = area.actions[0]?.id || '';
};

const selectOperation = operation => {
  selectedOperationId.value = operation.id;
};

const refreshSnapshot = async () => {
  if (!selectedConnectionId.value) {
    snapshot.value = null;
    return;
  }

  isLoading.value = true;
  try {
    const { data } = await LaisWhatsappOperationsAPI.snapshot(
      selectedConnectionId.value
    );
    snapshot.value = data;
  } catch (error) {
    snapshot.value = null;
    useAlert(
      error.response?.data?.error ||
        'Não foi possível carregar os recursos do WhatsApp.'
    );
  } finally {
    isLoading.value = false;
  }
};

const splitList = value =>
  value
    .toString()
    .split(/[\n,;]/)
    .map(item => item.trim())
    .filter(Boolean);

const inferMediaKind = file => {
  if (file.type === 'image/webp') return 'sticker';
  if (file.type.startsWith('video/')) return 'video';
  return 'image';
};

const readFile = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.toString().split(',').pop());
    reader.onerror = () =>
      reject(new Error('Não foi possível ler este arquivo.'));
    reader.readAsDataURL(file);
  });

const onFileChange = async (event, field) => {
  const [file] = event.target.files || [];
  if (!file) return;

  try {
    form[field.name] = await readFile(file);
    selectedFiles[field.name] = file.name;
    if (field.fileName) form[field.fileName] = file.name;
    if (field.mimetype)
      form[field.mimetype] = file.type || 'application/octet-stream';
    if (field.kind) form[field.kind] = inferMediaKind(file);
  } catch (error) {
    useAlert(error.message);
  }
};

const normalizedContactPhone = value => {
  const phone = value.toString().replace(/\D/g, '');
  if (phone.length < 10 || phone.length > 15) {
    throw new Error('Informe um telefone de contato válido com DDI.');
  }
  return phone;
};

const contactVcard = (
  displayName = form.display_name,
  phoneValue = form.phone
) => {
  const name = displayName
    .toString()
    .trim()
    .replace(/[\\;,]/g, character => `\\${character}`);
  const phone = normalizedContactPhone(phoneValue);
  return `BEGIN:VCARD\nVERSION:3.0\nFN:${name}\nTEL;TYPE=CELL:+${phone}\nEND:VCARD`;
};

const pipeValues = value => value.split('|').map(item => item.trim());

const buttonPayload = () => {
  const buttons = splitList(form.buttons).map(line => {
    const [id, title] = pipeValues(line);
    if (!id || !title) {
      throw new Error(
        'Cada botão precisa de identificador e título separados por |.'
      );
    }
    return { id, title };
  });
  if (
    buttons.length > 3 ||
    new Set(buttons.map(button => button.id)).size !== buttons.length
  ) {
    throw new Error(
      'Informe no máximo três botões com identificadores diferentes.'
    );
  }
  return buttons;
};

const listSectionsPayload = () => {
  const sections = new Map();
  splitList(form.sections).forEach(line => {
    const [sectionTitle, id, title, description] = pipeValues(line);
    if (!sectionTitle || !id || !title) {
      throw new Error(
        'Cada opção precisa de seção, identificador e título separados por |.'
      );
    }
    const section = sections.get(sectionTitle) || {
      title: sectionTitle,
      rows: [],
    };
    section.rows.push({ id, title, ...(description ? { description } : {}) });
    sections.set(sectionTitle, section);
  });
  return Array.from(sections.values());
};

const contactsPayload = () =>
  splitList(form.contacts).map(line => {
    const [display_name, phone] = pipeValues(line);
    if (!display_name || !phone) {
      throw new Error(
        'Cada contato precisa de nome e telefone separados por |.'
      );
    }
    return { display_name, vcard: contactVcard(display_name, phone) };
  });

const contactsJSONPayload = () => {
  let contacts;
  try {
    contacts = JSON.parse(form.contacts_json);
  } catch {
    throw new Error('Os contatos avançados precisam ser um JSON válido.');
  }

  if (
    !Array.isArray(contacts) ||
    contacts.length === 0 ||
    contacts.length > 100
  ) {
    throw new Error('Informe uma lista JSON com 1 a 100 contatos.');
  }

  return contacts.map(contact => {
    const entry = contact && typeof contact === 'object' ? contact : {};
    const displayName = entry.display_name?.toString().trim();
    const phone = entry.phone?.toString().trim();
    const vcard = entry.vcard?.toString().trim();
    if (!displayName || (!phone && !vcard)) {
      throw new Error(
        'Cada contato avançado precisa de display_name e phone ou vcard.'
      );
    }
    return { display_name: displayName, ...(vcard ? { vcard } : { phone }) };
  });
};

const supportsMessageContext = () =>
  selectedOperation.value.fields.some(field => field.context);

const buildMessageContext = payload => {
  if (form.forwarded !== 'unchanged') {
    payload.forwarded = form.forwarded === 'true';
  }

  if (form.forwarding_score !== '') {
    const forwardingScore = Number(form.forwarding_score);
    if (
      form.forwarded !== 'true' ||
      !Number.isInteger(forwardingScore) ||
      forwardingScore < 1 ||
      forwardingScore > 5
    ) {
      throw new Error(
        'O nível de encaminhamento precisa ser um número de 1 a 5 com o rótulo Encaminhada ativo.'
      );
    }
    payload.forwarding_score = forwardingScore;
  }

  const mentionedJids = splitList(form.mentioned_jids);
  if (mentionedJids.length > 100) {
    throw new Error('Informe no máximo 100 menções.');
  }
  if (mentionedJids.length) payload.mentioned_jids = mentionedJids;

  const reply = {
    messageId: form.reply_message_id.trim(),
    senderJid: form.reply_sender_jid.trim(),
    text: form.reply_text.trim(),
    message: form.reply_message.trim(),
  };
  if (!Object.values(reply).some(Boolean)) return;

  if (!reply.messageId || !reply.senderJid) {
    throw new Error(
      'Informe o ID e o JID do remetente da mensagem respondida.'
    );
  }
  if (reply.text && reply.message) {
    throw new Error(
      'Informe o texto ou o JSON protobuf da mensagem respondida, não ambos.'
    );
  }
  if (!reply.text && !reply.message) {
    throw new Error('Informe o conteúdo da mensagem respondida.');
  }

  payload.reply_to = {
    message_id: reply.messageId,
    sender_jid: reply.senderJid,
  };
  if (reply.message) {
    try {
      payload.reply_to.message = JSON.parse(reply.message);
    } catch {
      throw new Error(
        'A mensagem respondida em protobuf precisa ser um JSON válido.'
      );
    }
  } else {
    payload.reply_to.text = reply.text;
  }
};

const buildPayload = () => {
  const payload = {};
  selectedOperation.value.fields.forEach(field => {
    const value = form[field.name];
    if (field.context || field.inputOnly) return;
    if (field.type === 'checkbox' && field.optional && value === undefined)
      return;
    if (value === '' || value === null || value === undefined) return;

    if (field.type === 'list') {
      const items = splitList(value);
      payload[field.name] = field.integer
        ? items.map(item => Number(item))
        : items;
      return;
    }

    if (field.type === 'file') {
      payload[field.name] = value;
      if (field.fileName && form[field.fileName]) {
        payload[field.fileName] = form[field.fileName];
      }
      if (field.mimetype && form[field.mimetype]) {
        payload[field.mimetype] = form[field.mimetype];
      }
      if (field.kind && form[field.kind])
        payload[field.kind] = form[field.kind];
      return;
    }

    if (field.integer || field.type === 'number') {
      payload[field.name] = Number(value);
      return;
    }

    payload[field.name] = value;
  });

  if (supportsMessageContext()) buildMessageContext(payload);

  if (selectedOperation.value.id === 'send_contact') {
    payload.vcard ||= contactVcard();
  }
  if (selectedOperation.value.id === 'send_contacts') {
    payload.contacts = form.contacts_json.trim()
      ? contactsJSONPayload()
      : contactsPayload();
  }
  if (selectedOperation.value.id === 'send_buttons') {
    payload.buttons = buttonPayload();
  }
  if (selectedOperation.value.id === 'send_list') {
    payload.sections = listSectionsPayload();
  }
  if (selectedOperation.value.id === 'send_raw_message') {
    try {
      payload.message = JSON.parse(payload.message);
    } catch {
      throw new Error('A mensagem protobuf precisa ser um JSON válido.');
    }
  }

  return payload;
};

const validateForm = () => {
  const overLimit = selectedOperation.value.fields.find(field => {
    const value = form[field.name];
    return field.maxLength && value?.toString().length > field.maxLength;
  });

  if (overLimit) {
    useAlert(
      `${overLimit.label} aceita até ${overLimit.maxLength} caracteres.`
    );
    return false;
  }

  const missing = selectedOperation.value.fields.find(field => {
    if (!field.required || field.requiredWhen?.(form) === false) return false;
    const value = form[field.name];
    return field.type === 'list'
      ? splitList(value).length === 0
      : value === '' || value === null || value === undefined;
  });

  if (missing) {
    useAlert(`Preencha ${missing.label.toLowerCase()}.`);
    return false;
  }

  return true;
};

const executeOperation = async () => {
  if (!props.canManage) {
    useAlert('Apenas administradores podem alterar dados do WhatsApp.');
    return;
  }

  if (!isConnected.value) {
    useAlert('Conecte o WhatsApp por QR code antes de executar esta ação.');
    return;
  }

  if (!validateForm()) return;

  isExecuting.value = true;
  result.value = null;
  try {
    const { data } = await LaisWhatsappOperationsAPI.execute(
      selectedConnectionId.value,
      selectedOperation.value.id,
      buildPayload()
    );
    result.value = data.data || {};
    useAlert('Ação do WhatsApp concluída.');
    await refreshSnapshot();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        'Não foi possível concluir esta ação.'
    );
  } finally {
    isExecuting.value = false;
  }
};

watch(
  connectionOptions,
  options => {
    if (!options.some(option => option.id === selectedConnectionId.value)) {
      selectedConnectionId.value =
        options.find(
          option => String(option.inboxId) === String(props.preferredInboxId)
        )?.id ||
        options[0]?.id ||
        null;
    }
  },
  { immediate: true }
);

watch(selectedConnectionId, refreshSnapshot, { immediate: true });
watch(selectedOperationId, resetForm, { immediate: true });
watch(() => props.defaultTarget, resetForm);
watch(
  () => form.setting,
  setting => {
    if (selectedOperation.value?.id !== 'set_privacy') return;

    const values = privacyValues[setting] || [];
    if (!values.includes(form.value)) form.value = values[0] || '';
  }
);
watch(
  visibleActions,
  actions => {
    if (!actions.some(action => action.id === selectedOperationId.value)) {
      selectedOperationId.value = actions[0]?.id || '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Central do WhatsApp" prefix-icon="i-ph-whatsapp-logo-bold">
    <div class="grid gap-5">
      <div class="flex flex-col justify-between gap-3 lg:flex-row lg:items-end">
        <div class="max-w-2xl">
          <p class="text-[13px] leading-5 text-lais-text-body">
            Use formulários simples para mensagens, status, visto por último,
            grupos, comunidades e canais. As chamadas seguem o botão de telefone
            em Atendimentos.
          </p>
          <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
            Mensagens, controles e catálogo usam a sessão WhatsApp Web conectada
            por QR code.
          </p>
        </div>
        <div class="flex flex-wrap items-end gap-2">
          <LaisField label="Conexão" class="min-w-[220px]">
            <select v-model="selectedConnectionId" :class="input">
              <option :value="null">Selecione uma conexão</option>
              <option
                v-for="connection in connectionOptions"
                :key="connection.id"
                :value="connection.id"
              >
                {{ connection.label }}
              </option>
            </select>
          </LaisField>
          <button
            type="button"
            class="inline-flex h-11 items-center gap-2 rounded-lais-sm bg-lais-primary px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#3a2762]"
            @click="openCatalogManager"
          >
            <span class="i-lucide-shopping-bag size-4" />
            Gerenciar catálogo
          </button>
          <button
            type="button"
            class="inline-flex h-11 items-center gap-2 rounded-lais-sm border border-lais-border bg-white px-4 text-[13px] font-semibold text-lais-primary transition-colors hover:border-lais-primary/50 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isLoading || !selectedConnectionId"
            @click="refreshSnapshot"
          >
            <span
              class="size-4"
              :class="
                isLoading
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-refresh-cw'
              "
            />
            Atualizar
          </button>
        </div>
      </div>

      <div v-if="snapshot" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Sessão
          </p>
          <p class="mt-1 text-[15px] font-semibold text-lais-text">
            {{ isConnected ? 'Conectada' : 'Aguardando conexão' }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Grupos
          </p>
          <p class="mt-1 text-[15px] font-semibold text-lais-text">
            {{ groupCount }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Comunidades
          </p>
          <p class="mt-1 text-[15px] font-semibold text-lais-text">
            {{ communityCount }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Canais
          </p>
          <p class="mt-1 text-[15px] font-semibold text-lais-text">
            {{ newsletterCount }}
          </p>
        </div>
        <div class="rounded-lais border border-lais-border-soft bg-white p-4">
          <p
            class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Produtos
          </p>
          <p class="mt-1 text-[15px] font-semibold text-lais-text">
            {{ snapshot.catalog?.available ? catalogProductCount : '—' }}
          </p>
        </div>
      </div>

      <div
        v-if="snapshot && !isConnected"
        class="flex flex-col justify-between gap-3 rounded-lais border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center"
      >
        <div>
          <p class="text-[13px] font-semibold text-lais-text">
            O WhatsApp ainda não está conectado
          </p>
          <p class="mt-1 text-[12px] leading-5 text-lais-text-body">
            Leia um novo QR code para liberar envios, status, presença, grupos,
            canais, chamadas e o catálogo comercial.
          </p>
        </div>
        <button
          type="button"
          class="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#3a2762]"
          @click="emit('openConnection')"
        >
          Abrir conexão QR
        </button>
      </div>

      <div
        v-if="snapshot?.catalog"
        class="rounded-lais border p-4 text-[13px]"
        :class="
          snapshot.catalog.available && !snapshot.catalog.error
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-amber-200 bg-amber-50'
        "
      >
        <p class="font-semibold text-lais-text">Catálogo comercial</p>
        <p class="mt-1 leading-5 text-lais-text-body">
          {{
            snapshot.catalog.available
              ? snapshot.catalog.error ||
                'Disponível pela conexão WhatsApp Web desta conta.'
              : snapshot.catalog.reason
          }}
        </p>
      </div>

      <div
        v-if="!canManage"
        class="rounded-lais border border-lais-border-soft bg-lais-primary-50/40 p-4 text-[13px] leading-5 text-lais-text-body"
      >
        Você pode consultar a situação da sessão, mas somente administradores
        podem enviar mensagens ou alterar dados do WhatsApp.
      </div>

      <template v-else-if="snapshot">
        <div
          v-if="selectedAreaId === 'account'"
          class="rounded-lais border border-sky-200 bg-sky-50 p-4 text-[13px] leading-5 text-lais-text-body"
        >
          <p class="font-semibold text-lais-text">Foto da conta</p>
          <p class="mt-1">
            Esta sessão consulta a foto de perfil de contatos e grupos, mas não
            possui uma operação segura para trocar a foto da própria conta. Use
            as ações de mídia para enviar fotos a conversas e ao Status.
          </p>
        </div>

        <div class="flex gap-2 overflow-x-auto pb-1">
          <button
            v-for="area in operationAreas"
            :key="area.id"
            type="button"
            class="inline-flex shrink-0 items-center gap-2 rounded-lais-sm border px-3 py-2 text-[13px] font-semibold transition-colors"
            :class="
              selectedAreaId === area.id
                ? 'border-lais-primary bg-lais-primary text-white'
                : 'border-lais-border-soft bg-white text-lais-text-body hover:border-lais-primary/50'
            "
            @click="selectArea(area)"
          >
            <span class="size-4" :class="area.icon" />
            {{ area.label }}
          </button>
        </div>

        <div
          class="grid gap-5 xl:grid-cols-[minmax(260px,0.9fr)_minmax(0,1.6fr)]"
        >
          <div class="grid content-start gap-2">
            <button
              v-for="action in visibleActions"
              :key="action.id"
              type="button"
              class="rounded-lais border p-3 text-left transition-colors"
              :class="
                selectedOperationId === action.id
                  ? 'border-lais-primary bg-lais-primary-50'
                  : 'border-lais-border-soft bg-white hover:border-lais-primary/50'
              "
              @click="selectOperation(action)"
            >
              <p class="text-[13px] font-semibold text-lais-text">
                {{ action.label }}
              </p>
              <p class="mt-1 text-[12px] leading-4 text-lais-text-muted">
                {{ action.description }}
              </p>
            </button>
          </div>

          <div
            v-if="selectedOperation"
            class="rounded-lais border border-lais-border-soft bg-[#fcfbfd] p-4 sm:p-5"
          >
            <div
              class="flex flex-col justify-between gap-2 border-b border-lais-border-soft pb-4 sm:flex-row sm:items-start"
            >
              <div>
                <p class="text-[16px] font-semibold text-lais-text">
                  {{ selectedOperation.label }}
                </p>
                <p
                  class="mt-1 max-w-2xl text-[13px] leading-5 text-lais-text-body"
                >
                  {{ selectedOperation.description }}
                </p>
              </div>
              <span
                v-if="selectedOperation.danger"
                class="rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700"
              >
                Ação sensível
              </span>
            </div>

            <div
              v-if="selectedOperation.fields.length"
              class="mt-5 grid gap-4 md:grid-cols-2"
            >
              <LaisField
                v-for="field in selectedOperation.fields"
                :key="field.name"
                :label="field.label"
                :helper="field.helper"
                :required="field.required"
                :class="
                  field.type === 'textarea' ||
                  field.type === 'list' ||
                  field.type === 'file'
                    ? 'md:col-span-2'
                    : ''
                "
              >
                <textarea
                  v-if="field.type === 'textarea' || field.type === 'list'"
                  v-model="form[field.name]"
                  :rows="field.type === 'list' ? 4 : 5"
                  :placeholder="field.placeholder"
                  :class="input"
                />
                <select
                  v-else-if="field.type === 'select'"
                  v-model="form[field.name]"
                  :class="input"
                >
                  <option v-if="!field.required" value="">
                    Selecione uma opção
                  </option>
                  <option
                    v-for="option in fieldOptions(field)"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <div v-else-if="field.source" class="grid gap-2">
                  <input
                    v-model="form[field.name]"
                    :list="`whatsapp-${field.name}`"
                    :placeholder="field.placeholder"
                    :class="input"
                  />
                  <datalist :id="`whatsapp-${field.name}`">
                    <option
                      v-for="option in fieldOptions(field)"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </datalist>
                  <p
                    v-if="fieldOptions(field).length"
                    class="text-[12px] text-lais-text-muted"
                  >
                    {{
                      field.suggestionHelper ||
                      'Você pode selecionar uma sugestão ou informar outro JID.'
                    }}
                  </p>
                </div>
                <div
                  v-else-if="field.type === 'file'"
                  class="rounded-lais-sm border border-dashed border-lais-border bg-white p-3"
                >
                  <input
                    type="file"
                    class="block w-full text-[13px] text-lais-text-body file:mr-3 file:rounded-lais-sm file:border-0 file:bg-lais-primary-50 file:px-3 file:py-2 file:text-[12px] file:font-semibold file:text-lais-primary hover:file:bg-lais-primary-100"
                    :accept="field.accept"
                    @change="event => onFileChange(event, field)"
                  />
                  <p
                    v-if="selectedFiles[field.name]"
                    class="mt-2 text-[12px] text-lais-text-muted"
                  >
                    Arquivo selecionado: {{ selectedFiles[field.name] }}
                  </p>
                </div>
                <label
                  v-else-if="field.type === 'checkbox'"
                  class="flex min-h-11 items-center gap-3 rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text-body"
                >
                  <input
                    type="checkbox"
                    class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
                    :checked="form[field.name] === true"
                    @change="form[field.name] = $event.target.checked"
                  />
                  {{ field.label }}
                </label>
                <input
                  v-else
                  v-model="form[field.name]"
                  :type="field.type || 'text'"
                  :min="field.min"
                  :max="field.max"
                  :maxlength="field.maxLength"
                  :step="field.step"
                  :placeholder="field.placeholder"
                  :class="input"
                />
              </LaisField>
            </div>

            <div
              v-else
              class="mt-5 rounded-lais-sm border border-dashed border-lais-border bg-white p-4 text-[13px] leading-5 text-lais-text-body"
            >
              Esta ação não precisa de informações adicionais.
            </div>

            <div
              class="mt-5 flex flex-col justify-between gap-3 border-t border-lais-border-soft pt-4 sm:flex-row sm:items-center"
            >
              <p class="text-[12px] leading-5 text-lais-text-muted">
                {{
                  isConnected
                    ? 'A ação será executada na sessão WhatsApp selecionada.'
                    : 'Conecte a sessão por QR code para liberar esta ação.'
                }}
              </p>
              <button
                type="button"
                class="inline-flex h-11 items-center justify-center gap-2 rounded-lais px-4 text-[13px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                :class="
                  selectedOperation.danger
                    ? 'bg-lais-danger hover:bg-red-600'
                    : 'bg-lais-primary hover:bg-[#3a2762]'
                "
                :disabled="!canExecute"
                @click="executeOperation"
              >
                <span
                  class="size-4"
                  :class="
                    isExecuting
                      ? 'i-lucide-loader-circle animate-spin'
                      : 'i-lucide-check'
                  "
                />
                {{ isExecuting ? 'Executando...' : 'Confirmar ação' }}
              </button>
            </div>

            <div
              v-if="result"
              class="mt-4 rounded-lais-sm border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900"
            >
              <p class="font-semibold">Ação concluída</p>
              <p class="mt-1 break-all leading-5">{{ resultSummary }}</p>
            </div>
          </div>
        </div>

        <p class="text-[12px] leading-5 text-lais-text-muted">
          Mensagens de texto, anexos, enquetes, localização e contatos também
          ficam no compositor de Atendimentos. Mensagens brutas de protocolo não
          são exibidas aqui porque não são uma ação segura para usuários comuns.
        </p>
      </template>

      <div
        v-else
        class="rounded-lais border border-dashed border-lais-border bg-[#fcfbfd] p-4 text-[13px] text-lais-text-body"
      >
        Selecione uma conexão WhatsApp para carregar os recursos disponíveis.
      </div>
    </div>
  </LaisCard>
</template>
